import type { QueryClient } from "@tanstack/react-query";
import type { createApi, EnvName, SectionQuestion, TransientPatchBody } from "./api";
import { questionKey, sectionAnswers } from "./formVisibility";
import { findRemarkMatch, parseRemarksList } from "./section/remarksList";
import { notifyBoardSync } from "./useBoardEvents";
import {
  dedupeVoiceCommands,
  resolveNavigateTarget,
  type SectionHandle,
  type VoiceCommandWire,
} from "./voiceDispatch";

type BoardApi = ReturnType<typeof createApi>;

type SectionPlan = {
  cleared: boolean;
  answers: Record<string, string[]>;
  outlook: Record<string, string>;
  tier: string | null | undefined;
  mustSubmit: boolean;
};

function blankAnswers(questions: SectionQuestion[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const q of questions) {
    out[questionKey(q)] = "";
  }
  return out;
}

function sectionPlan(code: string, plans: Map<string, SectionPlan>): SectionPlan {
  const key = code.trim().toUpperCase();
  let plan = plans.get(key);
  if (!plan) {
    plan = {
      cleared: false,
      answers: {},
      outlook: {},
      tier: undefined,
      mustSubmit: false,
    };
    plans.set(key, plan);
  }
  return plan;
}

function absorbCommand(plans: Map<string, SectionPlan>, cmd: VoiceCommandWire): void {
  if (cmd.type === "patch") {
    const plan = sectionPlan(cmd.section_code, plans);
    for (const [id, val] of Object.entries(cmd.answers ?? {})) {
      (plan.answers[id] ??= []).push(val);
    }
    Object.assign(plan.outlook, cmd.outlook_48h);
    if (cmd.tier !== undefined) {
      plan.tier = cmd.tier;
    }
    plan.mustSubmit = true;
    return;
  }
  if (cmd.type === "clear") {
    const plan = sectionPlan(cmd.section_code, plans);
    plan.cleared = true;
    plan.mustSubmit = true;
    return;
  }
  if (cmd.type === "submit") {
    sectionPlan(cmd.section_code, plans).mustSubmit = true;
  }
}

async function applySectionPlanViaHandle(
  plan: SectionPlan,
  handle: SectionHandle,
): Promise<void> {
  if (plan.cleared) {
    handle.clear();
  } else {
    for (const [id, ops] of Object.entries(plan.answers)) {
      for (const val of ops) {
        handle.patch({ answers: { [id]: val } });
      }
    }
    if (Object.keys(plan.outlook).length > 0) {
      handle.patch({ outlook_48h: { ...plan.outlook } });
    }
    if (plan.tier !== undefined) {
      handle.patch({ tier: plan.tier });
    }
  }
  await Promise.resolve(handle.submit());
}

/** Headless board writes via REST (voice automation). */
export async function executeBoardCommands(
  commands: VoiceCommandWire[],
  deps: {
    env: EnvName;
    api: BoardApi;
    qc: QueryClient;
    navigate: (path: string) => void;
    getHandle?: (sectionCode: string) => SectionHandle | null;
  },
): Promise<void> {
  const plans = new Map<string, SectionPlan>();
  const transientCreates: Extract<VoiceCommandWire, { type: "add_transient" }>[] = [];
  const transientUpdates: Extract<VoiceCommandWire, { type: "update_transient" }>[] = [];
  const transientClears: Extract<VoiceCommandWire, { type: "clear_transients" }>[] = [];
  const weatherSyncs: Extract<VoiceCommandWire, { type: "sync_weather" }>[] = [];

  for (const cmd of dedupeVoiceCommands(commands)) {
    if (cmd.type === "navigate") {
      deps.navigate(resolveNavigateTarget(deps.env, cmd.target));
      continue;
    }
    if (cmd.type === "add_transient") {
      transientCreates.push(cmd);
      continue;
    }
    if (cmd.type === "update_transient") {
      transientUpdates.push(cmd);
      continue;
    }
    if (cmd.type === "clear_transients") {
      transientClears.push(cmd);
      continue;
    }
    if (cmd.type === "sync_weather") {
      weatherSyncs.push(cmd);
      continue;
    }
    absorbCommand(plans, cmd);
  }

  for (const cmd of transientCreates) {
    const { type: _type, ...body } = cmd;
    await deps.api.createTransient(body);
  }

  for (const cmd of transientUpdates) {
    const body: TransientPatchBody = {};
    const fields = [
      "callsign",
      "aircraft_type",
      "origin",
      "eta_zulu",
      "etd_zulu",
      "destination",
      "parking_spot",
    ] as const;
    for (const field of fields) {
      const value = cmd[field];
      if (value != null) body[field] = value;
    }
    await deps.api.updateTransient(cmd.transient_id, body);
  }

  for (const cmd of transientClears) {
    const { type: _type, aircraft_type, callsign } = cmd;
    await deps.api.clearTransients({
      aircraft_type: aircraft_type || undefined,
      callsign: callsign || undefined,
    });
  }

  for (const cmd of weatherSyncs) {
    const result = await deps.api.syncWeather(cmd.icao || undefined);
    for (const inner of result.voice_plan?.commands ?? []) {
      if (inner.type === "patch" || inner.type === "clear" || inner.type === "submit") {
        absorbCommand(plans, inner);
      }
    }
  }

  const touched = [...plans.entries()].filter(([, plan]) => plan.mustSubmit);
  if (
    touched.length === 0 &&
    transientCreates.length === 0 &&
    transientUpdates.length === 0 &&
    transientClears.length === 0 &&
    weatherSyncs.length === 0
  ) {
    return;
  }

  for (const [code, plan] of touched) {
    const handle = deps.getHandle?.(code) ?? null;
    if (handle) {
      await applySectionPlanViaHandle(plan, handle);
      continue;
    }

    const form = await deps.api.sectionForm(code);
    const questions = form.questions ?? [];
    const auto = form.tier_preview.mode === "auto";
    const baseAnswers = plan.cleared ? blankAnswers(questions) : { ...(form.last_answers ?? {}) };
    const answerState: Record<string, string> = { ...baseAnswers };
    if (!plan.cleared) {
      for (const [id, ops] of Object.entries(plan.answers)) {
        for (const val of ops) {
          if (val.startsWith("+")) {
            const items = parseRemarksList(answerState[id] ?? "");
            const text = val.slice(1).trim();
            if (text) items.push(text);
            answerState[id] = JSON.stringify(items);
          } else if (val.startsWith("-")) {
            const items = parseRemarksList(answerState[id] ?? "");
            const query = val.slice(1).trim();
            const idx = findRemarkMatch(items, query);
            if (idx !== -1) items.splice(idx, 1);
            answerState[id] = JSON.stringify(items);
          } else {
            answerState[id] = val;
          }
        }
      }
    }
    const outlook = plan.cleared
      ? {}
      : { ...(form.last_outlook_48h ?? {}), ...plan.outlook };
    const tier = plan.cleared
      ? undefined
      : plan.tier ?? (auto ? undefined : form.tier_pick?.value ?? undefined);

    await deps.api.submitSection(
      code,
      sectionAnswers(questions, answerState),
      tier,
      outlook,
      form.revision ?? null,
    );
  }

  await Promise.all([
    deps.qc.refetchQueries({ queryKey: ["dashboard", deps.env] }),
    deps.qc.refetchQueries({ queryKey: ["brief", deps.env] }),
    ...touched.map(([code]) =>
      deps.qc.refetchQueries({ queryKey: ["section-form", deps.env, code] }),
    ),
    ...(weatherSyncs.length > 0
      ? [deps.qc.invalidateQueries({ queryKey: ["ingest-feeds", deps.env] })]
      : []),
  ]);
  notifyBoardSync(deps.env);
}
